/**
* Smart Bar 3 Style Buffer
*
* Stores original positions and styles of elements that need to be adjusted by Smart Bar.
* All values except transition are added back to the element when hiding smart bar
* All values are removed when removing styleBuffer entirely.
* `padding-top` is used on `document.body` to push it down for Smart Bar, all other elements use `top`
* Sumo elements are ignored when pushing down (or up) other elements
* Original data/styles for an element are stored as `sumoSmartBarData` in the elements `dataset` property.
*
* Transitions that are needed by smart mode are added on to any existing transitions,
*   so we (probably) don't overwrite any existing transitions they may have.
*
* Since Smart mode adds transition times to all bump down elements, it reuses existing original data if it is available.
*/

define([
  'underscore',
], (_) => {
  let topElements = [];
  let bottomElements = [];

  const sumoClasses = [
    'sumome-react-modal-container',
    'sumome-dialog-popup',
    'sumome-dialog-popup-overlay',
    'sumome-image-sharer',
    'sumome-contactform-left-page-hidden',
    'sumome-contactform-right-page-hidden',
    'sumome-react-wysiwyg-component',
    'sumome-smartbar-powered-by-variant',
    'sumo-review-modal',
    'sumo-reply-modal',
    'sumo-question-modal',
  ];

  const bottomShareBarClasses = [
    'sumome-share-client-wrapper-desktop-bottom-bar',
    'sumome-share-client-wrapper-mobile-bottom-bar',
    'sumome-share-client-open-mobile-left',
    'sumome-share-client-open-mobile-right',
  ];

  const getSmartBarData = el => el.dataset ? el.dataset.sumoSmartBarData : el.getAttribute('data-sumo-smart-bar-data');
  const setSmartBarData = (el, val) => {
    if (el.dataset) {
      el.dataset.sumoSmartBarData = val;
    } else {
      el.setAttribute('data-sumo-smart-bar-data', val);
    }
  };

  const isSumoElement = (element) => {
    const className = element.getAttribute('class') || '';
    const classArr = className.split(' ');

    if (_.intersection(['s', 'u', 'm', 'o', 'me'], classArr).length === 5) {
      return true;
    }

    if (_.intersection(sumoClasses, classArr).length) {
      return true;
    }

    return false;
  };

  const isNotOnPage = (element) => {
    const style = window.getComputedStyle(element);
    if (
      style.getPropertyValue('visibility') === 'hidden' ||
      style.getPropertyValue('opacity') === 0 ||
      style.getPropertyValue('display') === 'none' ||
      element.offsetTop >= document.body.offsetWidth ||
      element.offsetLeft + element.offsetWidth <= 0 ||
      element.offsetLeft >= document.body.offsetWidth ||
      element.offsetLeft <= -element.offsetWidth ||
      element.offsetTop + element.offsetHeight <= 0
    ) {
      return true;
    } else {
      const p = element.parentNode;
      if (p && p !== document.body && p !== document.documentElement) {
        return isNotOnPage(p);
      }

      return false;
    }
  };

  const parseSumoData = (element) => {
    const sumoSmartBarData = getSmartBarData(element);
    if (!sumoSmartBarData) return {};

    let data;
    try {
      data = JSON.parse(sumoSmartBarData);
    } catch (e) {
      console.log('JSON parse error:', e);
      data = {};
    }
    return data;
  };

  const applyOriginalStyles = (element, applyAll) => {
    const data = parseSumoData(element);
    if (data && Object.keys(data)) {
      const originalStyle = data.originalInlineStyles;

      _.extend(element.style, originalStyle);

      if (applyAll) {
        // Also restore original transition
        element.style.transition = data.originalInlineTransition;
      }
    }
  };

  const storeOriginalStyles = (element, showType) => {
    const style = window.getComputedStyle(element);
    const existingData = parseSumoData(element);
    const data = {};

    // If smart bar and original data exists use that
    if (showType && (showType === 'smart' || showType === 'fixed') && Object.keys(existingData).length) {
      setSmartBarData(element, JSON.stringify(_.extend({}, existingData)));
      return;
    }

    // Store original _inline_ style
    data.originalInlineStyles = {
      top: element.style.top,
      bottom: element.style.bottom,
      marginTop: element.style.marginTop,
      marginBottom: element.style.marginBottom,
    };

    // Store original style
    data.originalOffset = {
      top: style.getPropertyValue('top'),
      bottom: style.getPropertyValue('bottom'),
    };

    data.originalOffsetTop = element.offsetTop;
    data.originalTransition = style.getPropertyValue('transition');
    data.originalInlineTransition = element.style.transition;
    data.originalMarginTop = style.getPropertyValue('margin-top');
    data.originalMarginBottom = style.getPropertyValue('margin-bottom');
    setSmartBarData(element, JSON.stringify(data));
  };

  const styleBufferTop = (smartBarEl, options) => {
    const smartBarHeight = smartBarEl ? smartBarEl.offsetHeight : 0;
    const elements = document.body.querySelectorAll('*');
    const showType = options.showType;
    const scrollTop = Math.max(document.body.scrollTop, document.documentElement.scrollTop);
    const scrollHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);

    if (
      scrollHeight !== window.innerHeight &&
      scrollTop + window.innerHeight >= scrollHeight - smartBarHeight
    ) {
      // Weird things happen when removing and
      // readding padding this close to the bottom so ignore style buffer :yn:
      // Store body style from resize if static
      if (showType === 'static' && options.position === 'top') {
        applyOriginalStyles(document.body, true);
        storeOriginalStyles(document.body, showType);
      }
      return;
    }

    // Apply original positioning before going further
    if (topElements && topElements.length) {
      _.each(topElements, (element) => {
        applyOriginalStyles(element);
      });
    }

    // Apply original styles to body
    applyOriginalStyles(document.body);

    // If no smartbar, there isn't anything to buffer
    if (!smartBarEl) {
      return;
    }

    // Get new list of topElements
    topElements = _.filter(elements, (element) => {
      const style = window.getComputedStyle(element);
      const position = style.getPropertyValue('position');
      const display = style.getPropertyValue('display');
      const className = element.getAttribute('class') || '';

      if (className.indexOf('sumome-react-form-tab-top-') > -1) return true;

      // Filter out sumo elements and their children
      if (element === smartBarEl || element.getAttribute('id') === 'sumome-modal-mask' || isSumoElement(element)) {
        return false;
      }

      if (display === 'none') return false;

      let p = element.parentNode;
      while (p && p !== document.body && p !== document.documentElement) {
        const parentClass = p.getAttribute('class') || '';
        if (p === smartBarEl || p.getAttribute('id') === 'sumome-modal-mask' || isSumoElement(p)) {
          return false;
        }
        p = p.parentNode;
      }

      if (['fixed', 'absolute'].indexOf(position) > -1) {
        // Get original values if they exist
        let offsetTop = element.offsetTop;
        if (showType === 'smart' || showType === 'fixed') {
          const data = parseSumoData(element);
          offsetTop = (typeof data.originalOffsetTop !== 'undefined') ? data.originalOffsetTop : offsetTop;
        }

        if (offsetTop + element.offsetHeight >= 0 && offsetTop < smartBarHeight && offsetTop > -2000) {
          return true;
        }
      }

      /**
       * Check for main tag and update styles
       * Should only have one main element max (without hidden attribute) per html spec
       */
      if (element.tagName === 'MAIN') {
        return true;
      }
    });

    // Check parents of top elements that are absolute positioned
    topElements = _.filter(topElements, (element) => {
      const style = window.getComputedStyle(element);
      const position = style.getPropertyValue('position');
      const id = element.getAttribute('id');

      if (position === 'fixed') return true;

      const relativeParents = [];
      let p = element.parentNode;
      while (p && p !== document.documentElement && p !== document.body) {
        const parentStyle = window.getComputedStyle(p);
        const parentPosition = parentStyle.getPropertyValue('position');

        if (['relative', 'absolute'].indexOf(parentPosition) > -1) {
          relativeParents.unshift(p);
        }

        p = p.parentNode;
      }

      if (relativeParents && relativeParents.length) {
        return false;
      } else {
        return true;
      }
    });

    // Filter elements that have parents already in the array
    topElements = _.filter(topElements, (element) => {
      let p = element.parentNode;
      while (p && p !== document.body && p !== document.documentElement) {
        if (topElements.indexOf(p) > -1) {
          // Element is already being pushed down by parent element
          return false;
        }

        p = p.parentNode;
      }

      return true;
    });

    // Store original styles
    _.each(topElements, (element) => {
      storeOriginalStyles(element, showType);
    });

    // Push down top elements
    _.each(topElements, (element) => {
      const style = window.getComputedStyle(element);
      const transition = style.getPropertyValue('transition');
      const data = parseSumoData(element);
      let originalTop = data.originalOffsetTop || 0;

      if (data.originalOffset.top && data.originalOffset.top.indexOf('px') > -1) {
        // Use top style instead of offset top
        originalTop = parseInt(data.originalOffset.top);
      }

      // Check for smart mode
      if (showType === 'smart') {
        element.style.transition = `${transition ? `${transition}, ` : ''}top .2s ease-in-out`;
      }

      if (element.tagName === 'MAIN' && data.originalMarginTop) {
        const bodyData = parseSumoData(document.body);
        const originalBodyMarginTop = (bodyData.originalMarginTop)
          ? parseInt(bodyData.originalMarginTop, 10)
          : 0;
        const marginNum = parseInt(data.originalMarginTop, 10);
        const totalHeight = `${marginNum + smartBarHeight + originalBodyMarginTop}px`;

        element.style.marginTop = totalHeight;
      } else {
        element.style.top = `${originalTop + smartBarHeight}px`;
      }
    });

    // Store original body styles
    storeOriginalStyles(document.body, showType);

    // Add padding-top to body
    const bodyStyle = window.getComputedStyle(document.body);
    const bodyTransition = bodyStyle.getPropertyValue('transition');

    // Get original padding of body (if it exists) before doing anything else
    const bodyData = parseSumoData(document.body);
    const originalBodyMarginTop = (bodyData.originalMarginTop && bodyData.originalMarginTop.indexOf('px') > -1) ? parseInt(bodyData.originalMarginTop) : 0;

    // Add bar height to padding-top
    if (document.body.style.transition.indexOf('margin-top .2s ease-in-out') > -1) {
      // Avoid adding more and more transition values
      document.body.style.transition = `${bodyTransition ? `${bodyTransition}, ` : ''}margin-top .2s ease-in-out`;
    }

    document.body.style.marginTop = `${smartBarHeight + originalBodyMarginTop}px`;
  };

  const styleBufferBottom = (smartBarEl, options) => {
    const smartBarHeight = smartBarEl ? smartBarEl.offsetHeight : 0;
    const elements = document.body.querySelectorAll('*');
    const showType = options.showType;

    // Apply original positioning before going further
    if (bottomElements && bottomElements.length) {
      _.each(bottomElements, (element) => {
        applyOriginalStyles(element);
      });
    }

    // If no smartbar, there isn't anything to buffer
    if (!smartBarEl) {
      return;
    }

    // Get new list of bottomElements
    bottomElements = _.filter(elements, (element) => {
      const style = window.getComputedStyle(element);
      const position = style.getPropertyValue('position');
      const className = element.getAttribute('class') || '';

      if (className.indexOf('sumome-react-form-tab-bottom-') > -1) return true;

      if (_.intersection(className.split(' '), bottomShareBarClasses).length) {
        return true;
      }

      // Filter out sumo elements
      if (element === smartBarEl || element.getAttribute('id') === 'sumome-modal-mask' || isSumoElement(element)) {
        return false;
      }

      // Ignore elements as tall or taller than viewport, they are most likely nav menus
      if (element.offsetHeight >= document.body.offsetHeight) {
        return false;
      }

      if (position === 'fixed') {
        // Check if element is on page, and in the way of smart bar
        if (!isNotOnPage(element) && element.offsetTop < document.body.offsetHeight && element.offsetTop + element.offsetHeight > document.body.offsetHeight - smartBarHeight) {
          return true;
        }
      }

      return false;
    });

    // Store original styles
    _.each(bottomElements, (element) => {
      storeOriginalStyles(element, showType);
    });

    // Push up bottom elements
    _.each(bottomElements, (element) => {
      const style = window.getComputedStyle(element);
      const transition = style.getPropertyValue('transition');
      const data = parseSumoData(element);
      const originalTop = data.originalOffsetTop;
      const className = element.getAttribute('class') || '';
      let bottom = document.body.offsetHeight - (originalTop + element.offsetHeight);

      // Check for smart mode
      if (showType === 'smart') {
        element.style.transition = `${transition}, top .2s ease-in-out, bottom .2s ease-in-out`;
      }

      if (_.intersection(className.split(' '), bottomShareBarClasses).length) {
        bottom = 0;
      }

      element.style.top = 'auto';
      element.style.bottom = `${bottom + smartBarHeight}px`;
    });

    if (showType === 'fixed') {
      // Store original body styles
      storeOriginalStyles(document.body, showType);

      // Get original padding of body (if it exists) before doing anything else
      const bodyData = parseSumoData(document.body);
      const originalBodyMarginBottom = (bodyData.originalMarginBottom && bodyData.originalMarginBottom.indexOf('px') > -1) ? parseInt(bodyData.originalMarginBottom) : 0;

      document.body.style.marginBottom = `${smartBarHeight + originalBodyMarginBottom}px`;
    }
  };

  const styleBuffer = (smartBarEl, options) => {
    if (!smartBarEl) return;

    options = options || { showType: 'fixed', position: 'top' };
    options.showType = options.showType || 'fixed';
    options.position = options.position || 'top';

    if (options.position === 'top') {
      // StyleBufferTop
      styleBufferTop(smartBarEl, options);
    } else if (options.showType !== 'static') {
      // StyleBufferBottom
      styleBufferBottom(smartBarEl, options);
    }
  };

  // Move all fixed elements back down after smart bar is reshown
  const smartShow = (smartBarEl, position) => {
    const smartBarHeight = smartBarEl.offsetHeight;
    _.each(position === 'top' ? topElements : bottomElements, (element) => {
      const data = parseSumoData(element);
      const originalTop = data.originalOffsetTop;
      element.style.top = position === 'top' ? `${originalTop + smartBarHeight}px` : 'auto';

      if (position === 'bottom') {
        const originalBottom = document.body.offsetHeight - (originalTop + element.offsetHeight);
        element.style.bottom = `${originalBottom + smartBarHeight}px`;
      }
    });
  };

  /**
  * Move all fixed elements back to original position while smart bar is hidden
  */
  const smartHide = (position) => {
    // Animate all top elements to original position
    if (position === 'top') {
      _.each(topElements, (element) => {
        const style = window.getComputedStyle(element);
        const position = style.getPropertyValue('position');

        if (position === 'fixed') {
          applyOriginalStyles(element);
        }
      });
    } else {
      _.each(bottomElements, (element) => {
        // Only apply original bottom. Top should be set to auto already.
        const data = parseSumoData(element);
        element.style.bottom = data.originalOffset.bottom;
      });
    }
  };

  /**
  * Handle static show_type scroll event
  *
  * The main purpose of this function is to reposition fixed elements
  * that are positioned incorrectly initially (i think)
  */
  const staticScroll = (smartBarEl) => {
    if (!smartBarEl) return;

    const delta = Math.max(
      smartBarEl.offsetHeight - (document.documentElement.scrollTop || document.body.scrollTop),
      0,
    );

    _.each(topElements, (element) => {
      const style = window.getComputedStyle(element);
      const position = style.getPropertyValue('position');
      if (position !== 'fixed') return;

      const data = parseSumoData(element);
      const originalTop = data.originalOffsetTop;

      const newTop = Math.max(smartBarEl.offsetHeight, originalTop + delta);

      element.style.transition = 'top 500ms';
      setTimeout(() => {
        element.style.top = `${newTop}px`;
      });
    });
  };

  /**
  * Resets ALL styles on effected elements to original styles
  */
  const removeStyleBuffer = (position) => {
    const elements = position === 'top' ? topElements : bottomElements;
    _.each(elements, (element) => {
      applyOriginalStyles(element);

      setTimeout(() => {
        // Wait half a second to remove any transitions we added
        applyOriginalStyles(element, true);
      }, 500);
    });

    applyOriginalStyles(document.body, true);
  };

  return {
    styleBuffer,
    smartShow,
    smartHide,
    staticScroll,
    removeStyleBuffer,
  };
});
